import React, { useEffect, useState } from 'react';
import {
    Box,
    Text,
    Table,
    Thead,
    Tbody,
    Tr,
    Th,
    Td,
    Button,
    IconButton,
    useDisclosure,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalFooter,
    ModalBody,
    ModalCloseButton,
    SimpleGrid,
    FormControl,
    FormLabel,
    Input,
    Tooltip,
    Flex, Icon, MenuItem, MenuDivider, MenuList, MenuButton, Menu,
    useColorModeValue,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon, ViewIcon, RepeatIcon, CopyIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { getProducts, deleteProduct } from '../../../api/productsApi';
import { useNavigate } from 'react-router-dom';
import { useNotification } from "../../../contexts/NotificationContext";
import axiosInstance from '../../../api/axiosInstance';
import CopyButton from "../../../components/copyButton/CopyButton";
import Pagination from '../orders/Pagination';
import FormPreview from '../../../components/formPreview/FormPreview';
import {MdSync} from "react-icons/md";
import useHasPermission from "../../../hooks/useHasPermission";
import {HiOutlineDotsVertical} from "react-icons/hi";
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../../contexts/AuthContext';

const ProductList = () => {
    const { t } = useTranslation()
    const tr = (key, defaultValue) => t(key, { defaultValue })
    const [products, setProducts] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [lastPage, setLastPage] = useState(1);
    const [pagination, setPagination] = useState({});
    const [loadingProductId, setLoadingProductId] = useState(null);
    const [productToDelete, setProductToDelete] = useState(null);
    const [previewProduct, setPreviewProduct] = useState(null);
    const [shouldSearch, setShouldSearch] = useState(0);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();
    const { addNotification } = useNotification();
    const hasPermission = useHasPermission()
    const { user } = useAuth()
    const canDeleteProducts = hasPermission('delete-products')
    const canEditProducts = hasPermission('edit-products')
    const canEditForms = hasPermission('edit-forms')
    // TEMP: landing builder restricted to user id 2 only
    const canUseLandingBuilder = hasPermission('premium') && canEditForms && user?.id === 2
    const { addErrorNotifications } = useNotification()
    const cardBg = useColorModeValue('white', 'navy.800')

    const {
        isOpen: isDeleteDialogOpen,
        onOpen: onOpenDeleteDialog,
        onClose: onCloseDeleteDialog,
    } = useDisclosure();

    const {
        isOpen: isPreviewDialogOpen,
        onOpen: onOpenPreviewDialog,
        onClose: onClosePreviewDialog,
    } = useDisclosure();

    useEffect(() => {
        fetchProducts();
    }, [currentPage, shouldSearch]);

    const fetchProducts = async () => {
        try {
            const response = await getProducts(currentPage, searchTerm);
            setProducts(response.data);
            setCurrentPage(response.current_page);
            setLastPage(response.last_page);
            setPagination(response);
        } catch (e) {
            addErrorNotifications(e)
        }
    };

    const handleDelete = async () => {
        if (productToDelete) {
            const response = await deleteProduct(productToDelete.id);
            setProducts(products.filter(product => product.id !== productToDelete.id));
            onCloseDeleteDialog();
            addNotification(response.message, 'success');
        }
    };

    const handlePageChange = (value) => {
        setCurrentPage(value);
    };

    const handleEdit = (id) => {
        navigate(`/admin/edit-product/${id}`);
    };

    const handleDuplicate = (id) => {
        navigate(`/admin/duplicate-product/${id}`);
    };

    const handleClickOpenDeleteDialog = (product) => {
        setProductToDelete(product);
        onOpenDeleteDialog();
    };

    const handleViewForm = async (productId) => {
        setLoadingProductId(productId);
        try {
            const response = await axiosInstance.get(`/products/${productId}/forms`);
            if (response.data.length > 0) {
                navigate(`/admin/forms/${response.data[0].id}/${productId}`);
            } else {
                navigate(`/admin/products/${productId}/create-form`);
            }
        } catch (error) {
            addErrorNotifications(error.response?.data || error);
        } finally {
            setLoadingProductId(null);
        }
    };

    const handlePreviewForm = (product) => {
        if (product.form) {
            setPreviewProduct(product);
            onOpenPreviewDialog();
        } else {
            addNotification(tr('products.messages.noForm', 'This product does not have a form'), 'warning');
        }
    };

    const getScriptShortCode=()=> previewProduct?`<div id="form-container"></div><script>(function(){var t="v="+Date.now(),e=document.createElement("script");e.src="${process.env.REACT_APP_API_DOMAIN}/js/form.js?"+t,document.head.appendChild(e),e.onload=function(){loadForm("${previewProduct.slug}","form-container","${process.env.REACT_APP_API_DOMAIN}","${previewProduct.api_key}")}})();</script>`:void 0;

    const handleSearchChange = (event) => {
        setSearchTerm(event.target.value);
        if (event.target.value === '') {
            setShouldSearch((prev) => prev + 1);
        }
    };

    const handleSearchDatabase = (event) => {
        if (event.key === 'Enter') {
            setShouldSearch((prev) => prev + 1);
        }
    };

    return (
        <Box maxWidth="xxl" px={{ base: '20px', md: '40px' }} py={{ base: '130px', md: '80px' }}>

            <Box bg={cardBg} p={4} boxShadow="lg" rounded="md" mb="20px">
                <SimpleGrid columns={{ base: 1, md: 2, lg: 2, xl: 3 }} spacing="20px">
                    <FormControl>
                        <FormLabel>{tr('products.searchProduct', 'Search Product')}</FormLabel>
                        <Input
                            placeholder={tr('products.searchByName', 'Search by name')}
                            onChange={handleSearchChange}
                            onKeyPress={handleSearchDatabase}
                            value={searchTerm}
                        />
                    </FormControl>

                    <Box
                        gridColumn={{ base: "span 1", md: "span 2" }}
                        textAlign={{ base: "center", md: "right" }}
                        mt={{ base: "20px", md: "0" }}
                    >
                        {canEditProducts && <Button
                            colorScheme="blue"
                            leftIcon={<Icon as={MdSync} />}
                            onClick={() => navigate('/admin/create-product')}
                            width={{ base: "full", md: "auto" }}
                        >
                            {tr('products.createProduct', 'Create Product')}
                        </Button>}
                    </Box>
                </SimpleGrid>
            </Box>

            <Box overflowX="auto" boxShadow="lg" rounded="md" bg={cardBg}>

                <Table variant="simple">
                    <Thead>
                        <Tr>
                            <Th>ID</Th>
                            <Th>{tr('products.table.name', 'Name')}</Th>
                            <Th>{tr('products.table.slug', 'Slug (Unique)')}</Th>
                            <Th>{tr('products.table.productId', 'Product ID')}</Th>
                            <Th>{tr('products.table.link', 'Link')}</Th>
                            <Th>{tr('products.table.options', 'Options')}</Th>
                            <Th>{tr('products.table.actions', 'Actions')}</Th>
                        </Tr>
                    </Thead>
                    <Tbody>
                        {products.map((product) => (
                            <Tr key={product.id}>
                                <Td>{product.id}</Td>
                                <Td>{product.name}</Td>
                                <Td>{product.slug}</Td>
                                <Td>{product.product_id}</Td>
                                <Td>
                                    {product.link ? (
                                        <Tooltip label={tr('products.openLink', 'Open Product Link')} hasArrow>
                                            <IconButton
                                                as="a"
                                                href={product.link}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                icon={<ExternalLinkIcon />}
                                                size="sm"
                                                variant="lightBrand"
                                                aria-label={tr('products.aria.productLink', 'Product Link')}
                                            />
                                        </Tooltip>
                                    ) : (
                                        <Text color="gray.400" fontSize="xs">{tr('products.noLink', 'No link')}</Text>
                                    )}
                                </Td>
                                <Td>
                                    <ul>
                                        {product.options.map(option => (
                                            <li key={option.id}>
                                                {option.name} - {option.value} {tr('products.currencyLei', 'LEI')}
                                            </li>
                                        ))}
                                    </ul>
                                </Td>
                                <Td>
                                    <Menu placement="left-start" isLazy>
                                        <MenuButton
                                            as={IconButton}
                                            size="sm"
                                            variant="ghost"
                                            icon={<HiOutlineDotsVertical />}
                                            aria-label={tr('products.aria.actions', 'Actions')}
                                        />

                                        <MenuList minW="180px" zIndex={20}>
                                            {canEditProducts && (
                                                <MenuItem
                                                    icon={<EditIcon />}
                                                    onClick={() => handleEdit(product.id)}
                                                >
                                                    {tr('products.editProduct', 'Edit product')}
                                                </MenuItem>
                                            )}

                                            {canEditProducts && (
                                                <MenuItem
                                                    icon={<CopyIcon />}
                                                    onClick={() => handleDuplicate(product.id)}
                                                >
                                                    {tr('products.duplicateProduct', 'Duplicate product')}
                                                </MenuItem>
                                            )}

                                            {canEditForms && (
                                                <>
                                                    <MenuItem
                                                        icon={<RepeatIcon />}
                                                        onClick={() => handleViewForm(product.id)}
                                                        isDisabled={loadingProductId === product.id}
                                                    >
                                                        {loadingProductId === product.id ? tr('products.loadingForm', 'Loading form...') : tr('products.editForm', 'Edit form')}
                                                    </MenuItem>

                                                    <MenuItem
                                                        icon={<ViewIcon />}
                                                        onClick={() => handlePreviewForm(product)}
                                                    >
                                                        {tr('products.previewForm', 'Preview form')}
                                                    </MenuItem>

                                                    {canUseLandingBuilder && (
                                                        <MenuItem
                                                            icon={<ExternalLinkIcon />}
                                                            onClick={() => navigate(`/admin/products/${product.id}/landing-page`)}
                                                        >
                                                            {tr('products.landingBuilder', 'Landing builder')}
                                                        </MenuItem>
                                                    )}
                                                </>
                                            )}

                                            {canDeleteProducts && <MenuDivider />}

                                            {canDeleteProducts && (
                                                <MenuItem
                                                    icon={<DeleteIcon />}
                                                    color="red.500"
                                                    onClick={() => handleClickOpenDeleteDialog(product)}
                                                >
                                                    {tr('products.deleteProduct', 'Delete product')}
                                                </MenuItem>
                                            )}
                                        </MenuList>
                                    </Menu>
                                </Td>
                            </Tr>
                        ))}
                    </Tbody>
                </Table>
            </Box>

            <Flex justifyContent="space-between" mt="4" flexWrap="wrap">
                <Text>
                    {tr('products.showing', 'Showing')} {pagination.to} {tr('products.of', 'of')} {pagination.total} {tr('products.products', 'products')}
                </Text>
                <Pagination totalPages={lastPage} currentPage={currentPage} onPageChange={handlePageChange} />
            </Flex>

            <Modal isOpen={isDeleteDialogOpen} onClose={onCloseDeleteDialog}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>{tr('products.deleteConfirmationTitle', 'Delete Confirmation')}</ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {tr('products.deleteConfirmation', 'Are you sure you want to delete this product?')}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onCloseDeleteDialog} mr={3}>
                            {tr('products.cancel', 'Cancel')}
                        </Button>
                        <Button colorScheme="red" onClick={handleDelete}>
                            {tr('products.delete', 'Delete')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>

            <Modal isOpen={isPreviewDialogOpen} onClose={onClosePreviewDialog} size="lg">
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {tr('products.formPreview', 'Form Preview')} <CopyButton textToCopy={getScriptShortCode()} />
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        {previewProduct && (
                            <FormPreview
                                productSlug={previewProduct.slug}
                                apiUrl={`${process.env.REACT_APP_API_DOMAIN}`}
                                apiKey={previewProduct.api_key}
                            />
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button onClick={onClosePreviewDialog}>
                            {tr('products.close', 'Close')}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        </Box>
    );
};

export default ProductList;